import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../i18n";
import "./Legal.css";

const Terms = () => {
  const { i18n } = useTranslation();
  const [activeSection, setActiveSection] = useState(null);

  const sections = [
    {
      id: 1,
      icon: "fa-solid fa-file-contract",
      title: "Agreement Overview",
      content: "These Terms of Service (the \"Agreement\") are an agreement between <strong>Solvio Tech</strong> (\"Solvio Tech\" or \"us\" or \"we\" or \"our\") and you (\"User\" or \"you\" or \"your\"). This Agreement sets forth the general terms and conditions of your use of the products and services made available by Solvio Tech and of the <a href='https://solviotech.com' class='legal-link'>solviotech.com</a> website (collectively, the \"Services\"). By using the Services, you agree to be bound by this Agreement."
    },
    {
      id: 2,
      icon: "fa-solid fa-layer-group",
      title: "Additional Policies and Agreements",
      content: "Use of the Services is also governed by the following policies, which are incorporated by reference. By using the Services, you also agree to the terms of the following policies:",
      bullets: [
        "Privacy Policy",
        "Cookie Policy"
      ],
      footer: "Additional terms may also apply to certain Services and are incorporated by reference herein as applicable."
    },
    {
      id: 3,
      icon: "fa-solid fa-user-check",
      title: "Account Eligibility",
      content: "By registering for or using the Services, you represent and warrant that:",
      bullets: [
        "You are eighteen (18) years of age or older. The Services are intended solely for Users who are 18 years of age or older",
        "If you use the Services on behalf of another party, you are authorized to bind such other party to this Agreement",
        "You will provide accurate, current, and complete information on any registration or contact forms, including a valid email address",
        "You agree to be fully responsible for all use of your account and for any actions that take place through your account",
        "You are responsible for maintaining the confidentiality of your password and account security"
      ]
    },
    {
      id: 4,
      icon: "fa-solid fa-briefcase",
      title: "Services",
      content: "Solvio Tech provides professional technology services including but not limited to:",
      bullets: [
        "Web Development",
        "Mobile App Development",
        "AI & Intelligent Systems",
        "Cloud & DevOps",
        "UI/UX Design & Prototyping",
        "Tech Consulting",
        "Google My Business & Analytics Setup",
        "Website Care & Maintenance",
        "Logo & Brand Design"
      ],
      footer: "All services are subject to a separate written project agreement which shall form part of these Terms. Our services are governed by the Kenya Information and Communications Act (KICA), 1998 as amended."
    },
    {
      id: 5,
      icon: "fa-solid fa-copyright",
      title: "Solvio Tech Content",
      content: "Except for User Content (as defined below), all content available through the Services — including designs, text, graphics, images, video, information, software, audio and other files, and their selection and arrangement — are the proprietary property of Solvio Tech or its licensors (collectively, \"Solvio Tech Content\"). <strong>Solvio Tech Content may not be modified, copied, distributed, reproduced, republished, downloaded, displayed, posted, transmitted, sold or exploited for any purpose</strong> in any form or by any means, in whole or in part, other than as expressly permitted in this Agreement."
    },
    {
      id: 6,
      icon: "fa-solid fa-upload",
      title: "User Content",
      content: "You may provide information, text, photos, videos, and other content to Solvio Tech in connection with the delivery of Services (collectively, \"User Content\"). You are solely responsible for any and all User Content. By providing User Content to Solvio Tech, you represent and warrant that:",
      bullets: [
        "You have all the necessary rights to provide such User Content",
        "Your User Content does not infringe or violate the rights of any third party",
        "Your User Content does not violate any applicable laws or regulations",
        "You grant Solvio Tech a non-exclusive licence to use User Content solely for the purpose of delivering the Services"
      ]
    },
    {
      id: 7,
      icon: "fa-solid fa-ban",
      title: "Prohibited Uses",
      content: "You may not use our Services to:",
      bullets: [
        "Violate any applicable laws or regulations in Kenya or internationally",
        "Infringe the intellectual property rights of Solvio Tech or any third party",
        "Transmit any unsolicited or unauthorized advertising or promotional material",
        "Impersonate any person or entity or misrepresent your affiliation",
        "Engage in any conduct that restricts or inhibits anyone's use of the Services",
        "Attempt to gain unauthorized access to any part of the Services or related systems"
      ]
    },
    {
      id: 8,
      icon: "fa-solid fa-money-bill-wave",
      title: "Payment Terms",
      content: "All payments for Solvio Tech services are subject to the following terms:",
      bullets: [
        "A deposit of 50% is required before commencement of any project",
        "The remaining balance is due upon project completion and before final delivery",
        "Payments may be made via M-Pesa, bank transfer, or other agreed payment methods",
        "All prices are quoted in Kenyan Shillings (KSh) unless otherwise agreed in writing",
        "Late payments may attract interest at the prevailing Central Bank of Kenya rate"
      ]
    },
    {
      id: 9,
      icon: "fa-solid fa-rotate-left",
      title: "Refund Policy",
      content: "Solvio Tech operates a fair refund policy:",
      bullets: [
        "Deposits are non-refundable once work has commenced",
        "If Solvio Tech fails to deliver agreed services, a pro-rata refund will be considered",
        "Refund requests must be submitted in writing within 14 days of the issue arising",
        "All refund decisions are at the sole discretion of Solvio Tech management"
      ]
    },
    {
      id: 10,
      icon: "fa-solid fa-triangle-exclamation",
      title: "Limitation of Liability",
      content: "To the maximum extent permitted by applicable Kenyan law, Solvio Tech shall not be liable for any <strong>indirect, incidental, special, consequential, or punitive damages</strong>, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of (or inability to access or use) the Services. Our total liability to you for any claims arising from these Terms or the Services shall not exceed the amount paid by you to Solvio Tech in the three (3) months preceding the claim."
    },
    {
      id: 11,
      icon: "fa-solid fa-gavel",
      title: "Governing Law & Disputes",
      content: "This Agreement shall be governed by and construed in accordance with the <strong>laws of Kenya</strong>. Any disputes arising out of or in connection with this Agreement shall first be subject to good-faith negotiation. If unresolved within 30 days, disputes shall be referred to mediation under the Nairobi Centre for International Arbitration (NCIA) rules. The courts of Kenya shall have exclusive jurisdiction for any matters not resolved through mediation."
    },
    {
      id: 12,
      icon: "fa-solid fa-pen-to-square",
      title: "Changes to These Terms",
      content: "Solvio Tech reserves the right to modify these Terms at any time. We will provide notice of significant changes by updating the date at the top of this page and, where appropriate, notifying you by email. Your continued use of the Services after any changes constitutes your acceptance of the new Terms. We encourage you to review these Terms periodically."
    },
    {
      id: 13,
      icon: "fa-solid fa-envelope",
      title: "Contact Information",
      content: "For any questions about these Terms of Service:",
      bullets: [
        "Email: info@solviotech.com",
        "Phone: +254 720 955 034 / +254 757 913 737",
        "Address: 1st Kaunda Street, Nairobi, Kenya 🇰🇪",
        "Business Hours: Monday–Friday, 9:00 AM – 6:00 PM EAT"
      ]
    }
  ];

  return (
    <div className="legal-page">

      {/* ── Hero ── */}
      <section className="legal-hero">
        <div className="legal-hero-bg">
          <div className="legal-orb legal-orb--1"></div>
          <div className="legal-orb legal-orb--2"></div>
          <div className="legal-hero-grid"></div>
        </div>
        <div className="legal-hero-inner">
          <div className="legal-breadcrumb">
            <Link to="/" className="legal-breadcrumb-link">Home</Link>
            <i className="fa-solid fa-chevron-right"></i>
            <span>Terms of Service</span>
          </div>
          {i18n.language !== "en" && (
          <div className="legal-lang-notice">
            <i className="fa-solid fa-circle-info"></i>
            {i18n.language === "sw"
              ? "Hati hii inapatikana kwa Kiingereza tu. Maandishi ya kisheria yanafunga kwa Kiingereza."
              : "Ce document est disponible en anglais uniquement. Le texte juridique est contraignant en anglais."}
          </div>
        )}
          <div className="legal-hero-badge">
            <i className="fa-solid fa-scale-balanced"></i>
            <span>Governed by the Laws of Kenya</span>
          </div>
          <h1 className="legal-hero-title">Terms of Service</h1>
          <p className="legal-hero-sub">
            Please read these terms carefully before using Solvio Tech services.
            They outline our mutual obligations and protect both parties.
          </p>
          <div className="legal-meta">
            <span><i className="fa-solid fa-calendar"></i> Last updated: May 2026</span>
            <span className="legal-meta-sep">·</span>
            <span><i className="fa-solid fa-location-dot"></i> Solvio Tech, Nairobi, Kenya</span>
            <span className="legal-meta-sep">·</span>
            <span><i className="fa-solid fa-language"></i> {sections.length} sections</span>
          </div>
        </div>
      </section>

      {/* ── Table of Contents ── */}
      <section className="legal-toc-section">
        <div className="legal-container">
          <div className="legal-toc">
            <h3 className="legal-toc-title"><i className="fa-solid fa-list"></i> Contents</h3>
            <div className="legal-toc-grid">
              {sections.map((s) => (
                <a key={s.id} href={`#section-${s.id}`} className="legal-toc-item">
                  <span className="legal-toc-num">{String(s.id).padStart(2, "0")}</span>
                  <span>{s.title}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Content ── */}
      <section className="legal-content-section">
        <div className="legal-container">
          <div className="legal-sections">
            {sections.map((s) => (
              <div
                key={s.id}
                id={`section-${s.id}`}
                className={`legal-section ${activeSection === s.id ? "legal-section--active" : ""}`}
                onClick={() => setActiveSection(activeSection === s.id ? null : s.id)}
              >
                <div className="legal-section-header">
                  <div className="legal-section-icon"><i className={s.icon}></i></div>
                  <div className="legal-section-meta">
                    <span className="legal-section-num">{String(s.id).padStart(2, "0")}</span>
                    <h2 className="legal-section-title">{s.title}</h2>
                  </div>
                </div>
                <div className="legal-section-body">
                  <p dangerouslySetInnerHTML={{ __html: s.content }}></p>
                  {s.bullets && (
                    <ul className="legal-bullets">
                      {s.bullets.map((b, i) => (
                        <li key={i}><i className="fa-solid fa-check"></i><span>{b}</span></li>
                      ))}
                    </ul>
                  )}
                  {s.footer && <p className="legal-section-footer">{s.footer}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="legal-cta">
        <div className="legal-cta-inner">
          <div className="legal-cta-orb"></div>
          <i className="fa-solid fa-handshake legal-cta-icon"></i>
          <h2 className="legal-cta-title">Questions About Our Terms?</h2>
          <p className="legal-cta-sub">We believe in clarity and fairness. Reach out if anything is unclear.</p>
          <div className="legal-cta-actions">
            <Link to="/contact" className="legal-btn-primary">
              <i className="fa-solid fa-paper-plane"></i> Contact Us
            </Link>
            <Link to="/privacy" className="legal-btn-secondary">
              Privacy Policy <i className="fa-solid fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Terms;
